import { ProductCarousel } from '@/components/blocks/ProductCarousel';
import { ProductCarouselSkeleton } from '@/components/blocks/ProductCarousel/parts/ProductCarouselSkeleton';
import { useCatalogEntryRecommendation } from '@/data/Content/CatalogEntryRecommendation';
import { useRecommendationProducts } from '@/data/Content/RecommendationProducts';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { COMMERCE_AI_PROJECT_KEY } from '@/data/constants/commerceAI';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { ITEM_LIST_IDS } from '@/data/constants/gtm';
import { ContentProvider } from '@/data/context/content';
import { EventsContext } from '@/data/context/events';
import { GTMContainerListType } from '@/data/types/GTM';
import { ProductType } from '@/data/types/Product';
import { WidgetComponentProps, WidgetProperties } from '@/data/types/Slot';
import { FC, useContext, useEffect, useMemo } from 'react';

const emptyProperties = {} as WidgetProperties;
const EMPTY_PARTNUMBERS: string[] = [];

export const CatalogEntryRecommendation: FC<WidgetComponentProps> = ({
	properties = emptyProperties,
}) => {
	const { emsName = '', titleInput } = properties;
	const {
		partNumbers = EMPTY_PARTNUMBERS,
		clickAction,
		aiModelData,
		title,
	} = useCatalogEntryRecommendation(emsName, properties);

	const productRecommendationTitle = title
		?.find((content) => content.text?.trim());

	const fallbackTitle = titleInput?.trim() || undefined;

	const finalTitle = productRecommendationTitle ?? fallbackTitle;

	const { onItemListView } = useContext(EventsContext);
	const { settings } = useSettings();
	const { products, loading, error } = useRecommendationProducts({
		id: partNumbers,
		condition: partNumbers.length > 0,
		profileName: SearchProfiles.HDM_FIND_PRODUCTS_BY_PART_NUMBER_EMARSYS_CAROUSEL,
	});

	const filteredProducts = useMemo(
		() => products?.filter((product): product is ProductType => !!product) ?? [],
		[products]
	);
	const onClickProduct = (product: ProductType) => clickAction.bind(null, product.partNumber)();

	const loc = useLocalization('productDetail');
	const listData: GTMContainerListType = useMemo(
		() => ({
			productListData: {
				listId: aiModelData?.length
					? `${settings.userData[COMMERCE_AI_PROJECT_KEY]}-${aiModelData.at(0)?.modelId}`
					: ITEM_LIST_IDS.PRODUCT_RECOMMENDATIONS,
				listName: aiModelData?.length
					? loc.commerceAIRecommendations.t()
					: loc.productRecommendations.t(),
			},
		}),
		[aiModelData, loc, settings.userData]
	);

	const ctxValue = useMemo(() => ({ ...listData }), [listData]);

	useEffect(() => {
		if (products && Array.isArray(products) && products.length > 0) {
			onItemListView({
				gtm: {
					products: products as ProductType[],
					listPageName: listData.productListData.listName as string,
					listId: listData.productListData.listId,
					storeName: settings.storeName,
					settings,
				},
			});
		}
	}, [products, listData, onItemListView, settings]);

	if (loading) {
		return <ProductCarouselSkeleton />;
	}

	if (error || filteredProducts.length < 1) return null;

	return (
		<ContentProvider value={ctxValue}>
			<ProductCarousel
				products={filteredProducts}
				title={finalTitle}
				onClickProduct={onClickProduct}
			/>
		</ContentProvider>
	);
};
